import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { setCustomerDefaultLandingPage, deleteCustomerDefaultLandingPage } from '@api/landingPage';
import { message } from 'antd';
import { selectedCustomerIdSelector } from '@store/common/selectors';
import getActualShareLink from '@components/common/Sharing/SharingModal/SharingContent/getActualShareLink';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';

/**
 * Hook for admin operations on customer default landing page
 */
export const useCustomerLevelDefaultLandingPageConfig = () => {
  const queryClient = useQueryClient();
  const customerId = useSelector(selectedCustomerIdSelector);
  const updateDefaultLandingPage = useDefaultLandingPageStore(
    (state) => state.updateDefaultLandingPage,
  );

  const setCustomerDefaultMutation = useMutation(
    (config: any) => setCustomerDefaultLandingPage(config),
    {
      onSuccess: (data, variables) => {
        message.success('Default Landing Page saved successfully');
        // Update the store with the new customer default
        const landingPageConfig = variables.configuration;
        updateDefaultLandingPage({
          ...landingPageConfig,
          isFromCustomerDefault: true,
        });
        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['defaultLandingPage']);
        queryClient.invalidateQueries(['customerDefaultLandingPage', customerId]);
      },
      onError: (error: any) => {
        message.error('Failed to save Default Landing Page');
        // eslint-disable-next-line no-console
        console.error('Error setting default landing page:', error);
      },
    },
  );

  const deleteCustomerDefaultMutation = useMutation(() => deleteCustomerDefaultLandingPage({}), {
    onSuccess: () => {
      message.success('Default Landing Page deleted successfully');
      // Update store to clear customer default flag
      updateDefaultLandingPage({ isFromCustomerDefault: false });
      // Invalidate queries to refresh data
      queryClient.invalidateQueries(['defaultLandingPage']);
      queryClient.invalidateQueries(['customerDefaultLandingPage', customerId]);
    },
    onError: (error: any) => {
      message.error('Failed to delete Default Landing Page');
      // eslint-disable-next-line no-console
      console.error('Error deleting default landing page:', error);
    },
  });

  const applyToAll = useCallback(async () => {
    // Get current page information
    const { pathname } = window.location;
    const pathSegments = pathname.split('/');
    const pageName = pathSegments[pathSegments.length - 1] || 'Dashboard';
    const shareableUrl = getActualShareLink(pathname, customerId);

    const landingPageConfig = {
      url: shareableUrl,
      name: pageName,
      path: pathname,
    };

    if (!landingPageConfig.url || !landingPageConfig.name || !landingPageConfig.path) {
      message.error('Unable to determine current page information');
      return;
    }

    return setCustomerDefaultMutation.mutateAsync({
      configuration: landingPageConfig,
    });
  }, [setCustomerDefaultMutation, customerId]);

  const removeCustomerDefault = useCallback(async () => {
    return deleteCustomerDefaultMutation.mutateAsync();
  }, [deleteCustomerDefaultMutation]);

  return {
    applyToAll,
    removeCustomerDefault,
    isSettingDefault: setCustomerDefaultMutation.isLoading,
    isDeletingDefault: deleteCustomerDefaultMutation.isLoading,
  };
};
